import React, { useMemo, useState } from 'react'
import { RightOutlined } from '@ant-design/icons'

import { useFiltersSearch } from '@app/hooks'
import { LntCollapse, LntCollapsePanel, LntDivider, LntSelect } from '@app/components/base'
import { LntCheckbox, LntInput, LntRangeDatePicker } from '@app/components/base/Form'
import { TopFilterItem, TopFilters as TopFiltersWrapper } from '@app/components'

import {
  getFilterCompetitorsFormEntities,
  getFilterFromEntitiesToLabelsIds,
  getFilterFromEntities
} from '@app/utils/entitiesAdapters'

import { getKinds, getTypes } from '@app/api/routes/monitorings'
import { getStores } from '@app/api/routes/locationStores'
import { getClusters } from '@app/api/routes/locationСlusters'
import { getSalesOrganization } from '@app/api/routes/locationSalesOrganization'
import { getCommodityGroups, getPurchasingGroups, getTrends } from '@app/api/routes/catalog'
import { getCompetitorsNetworks } from '@app/api/routes/locationCompetitorsNetworks'
import { getCompetitors } from '@app/api/routes/locationCompetitors'
import { getMonitoringListsPromo } from '@app/api/routes/monitoringListsPromo'

import { LOCATION_KEY, MONITORINGS_TYPES_KEY, CATALOG_KEY, MONITORINGS_LISTS_KEY } from '@app/constants/queryKeys'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { FILTERS_PARAMS_KEYS } from '@app/api/constants'
import { SIZE, dateFormat } from '@app/constants'

import { FilterPeriod, useFilterPeriod } from '@app/features/filterPeriod'

import { isString } from '@app/typeGuards'
import { stringRegexReplace } from '@app/utils/string'
import { isEmptyArray, isEmptyString } from '@app/utils'

import styles from './styles.module.scss'

import { getCollapseTitle, updateFiltersByKey } from './utils'
import {
  priceTypeOptions,
  commonSelectProps,
  commentsByUsersOptions,
  isMatchedWithProductOptions,
  abnormalStateOptions
} from './configs'

import type { TopFilterProps } from './types'
import type { ChangeEvent } from 'react'

const COLLAPSE_PANEL_KEY = 'additional-filters-collapse'

export const TopFilters: React.FC<TopFilterProps> = ({ onSearch, initialFilters }) => {
  const {
    filters,
    canResetFilters,
    setFilters,
    handleClearFilters,
    handleSearch: submitFilters,
    handleSelectFilterChange,
    handleCreatedRangeDateChange,
    handleCheckboxChange,
    handleInputFilterChange,
    handleWeekChange
  } = useFiltersSearch({ initialFilters, onSearch })

  const [collapseActiveKey, setCollapseActiveKey] = useState<string | string[]>([COLLAPSE_PANEL_KEY])
  const [parsedProductCodeValue, setParsedProductCodeValue] = useState(filters?.productCodes)

  const isFiltersShow = useMemo(
    () => (isString(collapseActiveKey) ? isEmptyString(collapseActiveKey) : !isEmptyArray(collapseActiveKey)),
    [collapseActiveKey]
  )

  const { isWeekSelected, selectedPeriodParam, handleClear, handlePeriodChange, handleRangeDateChange } =
    useFilterPeriod({
      filters,
      setFilters,
      onSearch,
      handleClearFilters,
      atAfterParamKey: FILTERS_PARAMS_KEYS.collectedDateAtAfter,
      atBeforeParamKey: FILTERS_PARAMS_KEYS.collectedDateAtBefore
    })

  const competitorsParams = useMemo(() => ({ networkIds: filters.competitorNetworkIds }), [filters])

  const onChangeInputProductCode = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value

    setParsedProductCodeValue(value)

    const REGEXP_PATTERN = /\D+/g
    const parsedString = stringRegexReplace(value, REGEXP_PATTERN)

    setFilters(prev => updateFiltersByKey(prev, FILTERS_PARAMS_KEYS.productCodes, parsedString))
  }

  const clear = (): void => {
    handleClear()
    setParsedProductCodeValue('')
  }

  const handleSearch = (): void => {
    submitFilters()
    setCollapseActiveKey([])
    setParsedProductCodeValue(filters?.productCodes)
  }

  return (
    <TopFiltersWrapper
      className={ styles.topFilters }
      canReset={ canResetFilters }
      onClear={ clear }
      onSearch={ handleSearch }
    >
      <LntSelect
        { ...commonSelectProps }
        value={ filters.monitoringKindIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.monitoringKindIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsKindFilter }
        querySelect={ {
          queryKey: [MONITORINGS_TYPES_KEY.kindsItems],
          queryFn: getKinds,
          select: getFilterFromEntities
        } }
      />

      <FilterPeriod
        filters={ filters }
        isWeekSelected={ isWeekSelected }
        selectedPeriodParam={ selectedPeriodParam }
        onWeekChange={ handleWeekChange }
        onPeriodChange={ handlePeriodChange }
        onRangeDateChange={ handleRangeDateChange }
        atAfterParamKey={ FILTERS_PARAMS_KEYS.collectedDateAtAfter }
        atBeforeParamKey={ FILTERS_PARAMS_KEYS.collectedDateAtBefore }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.monitoringTypeIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.monitoringTypeIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsTypeFilter }
        querySelect={ {
          queryKey: [MONITORINGS_TYPES_KEY.typesItems],
          queryFn: getTypes,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.storeIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.storeIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.storesFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationStoresItems],
          queryFn: getStores,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.clusterIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.clusterIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationClustersItems],
          queryFn: getClusters,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.salesOrganizationIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.salesOrganizationIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.salesOrganizationFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationSalesOrganizationsItems],
          queryFn: getSalesOrganization,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.competitorNetworkIds }
        onChange={ value =>
          handleSelectFilterChange(FILTERS_PARAMS_KEYS.competitorNetworkIds, value, [FILTERS_PARAMS_KEYS.competitorIds])
        }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationCompetitorsNetworksItems],
          queryFn: getCompetitorsNetworks,
          select: getFilterFromEntities
        } }
        placeholder={ FILTERS_PLACEHOLDERS.competitorsNetworksFilter }
      />

      <LntSelect
        { ...commonSelectProps }
        disabled={ !filters.competitorNetworkIds?.length }
        value={ filters.competitorIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.competitorIds, value) }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationCompetitorsItems, competitorsParams],
          queryFn: async () => await getCompetitors(competitorsParams),
          select: getFilterCompetitorsFormEntities
        } }
        placeholder={ FILTERS_PLACEHOLDERS.competitorsAddressesFilter }
      />

      <LntInput
        value={ parsedProductCodeValue }
        onChange={ event => onChangeInputProductCode(event) }
        placeholder={ FILTERS_PLACEHOLDERS.productCode }
        size={ SIZE.middle }
      />

      <div className={ styles.topFilters__fullWidthContainer }>
        <LntCollapse
          ghost
          className={ styles.topFilters__collapse }
          activeKey={ collapseActiveKey }
          onChange={ setCollapseActiveKey }
          expandIconPosition='end'
          expandIcon={ ({ isActive }) => <RightOutlined rotate={ isActive ? -90 : 0 } /> }
        >
          <LntCollapsePanel
            key={ COLLAPSE_PANEL_KEY }
            header={ getCollapseTitle(isFiltersShow) }
          >
            <LntDivider className={ styles.topFilters__divider } />
            <div className={ styles.topFilters__row }>
              <p className={ styles.group__title }>Ценовая разница</p>

              <LntSelect
                showArrow
                strictValue
                size={ SIZE.middle }
                value={ filters.priceType }
                options={ priceTypeOptions }
                onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.priceType, value) }
                placeholder={ FILTERS_PLACEHOLDERS.allPrices }
              />

              <TopFilterItem
                colspan={ 2 }
                className={ styles.topFiltersItem__separated }
              >
                <LntInput
                  value={ filters.priceDifferenceFrom }
                  onChange={ event => handleInputFilterChange(FILTERS_PARAMS_KEYS.priceDifferenceFrom, event) }
                  placeholder={ FILTERS_PLACEHOLDERS.allPricesFrom }
                  size={ SIZE.middle }
                  suffix='%'
                />

                <LntInput
                  value={ filters.priceDifferenceTo }
                  onChange={ event => handleInputFilterChange(FILTERS_PARAMS_KEYS.priceDifferenceTo, event) }
                  placeholder={ FILTERS_PLACEHOLDERS.allPricesTo }
                  size={ SIZE.middle }
                  suffix='%'
                />
              </TopFilterItem>

              <LntCheckbox
                value={ filters.excludeRangePriceDifference }
                onChange={ handleCheckboxChange(FILTERS_PARAMS_KEYS.excludeRangePriceDifference, true) }
              >
                Вне диапазона
              </LntCheckbox>

              <LntSelect
                { ...commonSelectProps }
                value={ filters.abnormalState }
                options={ abnormalStateOptions }
                onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.abnormalState, value) }
                placeholder={ FILTERS_PLACEHOLDERS.abnormalState }
              />
            </div>

            <LntDivider className={ styles.topFilters__divider } />

            <div className={ styles.topFilters__row }>
              <p className={ styles.group__title }>Товары</p>

              <LntInput
                value={ filters.productName }
                onChange={ event => handleInputFilterChange(FILTERS_PARAMS_KEYS.productName, event) }
                placeholder={ FILTERS_PLACEHOLDERS.productName }
                size={ SIZE.middle }
              />

              <LntSelect
                { ...commonSelectProps }
                value={ filters.trendIds }
                onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.trendIds, value) }
                placeholder={ FILTERS_PLACEHOLDERS.trendsFilter }
                querySelect={ {
                  queryKey: [CATALOG_KEY.trendsItems],
                  queryFn: getTrends,
                  select: getFilterFromEntitiesToLabelsIds
                } }
              />

              <LntSelect
                { ...commonSelectProps }
                value={ filters.purchasingGroupIds }
                onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.purchasingGroupIds, value) }
                placeholder={ FILTERS_PLACEHOLDERS.purchasingGroupsFilter }
                querySelect={ {
                  queryKey: [CATALOG_KEY.purchasingGroupsItems],
                  queryFn: getPurchasingGroups,
                  select: getFilterFromEntitiesToLabelsIds
                } }
              />

              <LntSelect
                { ...commonSelectProps }
                value={ filters.commodityGroupIds }
                onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.commodityGroupIds, value) }
                placeholder={ FILTERS_PLACEHOLDERS.commodityGroupsFilter }
                querySelect={ {
                  queryKey: [CATALOG_KEY.commodityGroupsItems],
                  queryFn: getCommodityGroups,
                  select: getFilterFromEntitiesToLabelsIds
                } }
              />

              <LntSelect
                showArrow
                size={ SIZE.middle }
                strictValue
                placeholder={ FILTERS_PLACEHOLDERS.isMatchedWithProduct }
                value={ filters.isMatchingStatus }
                options={ isMatchedWithProductOptions }
                onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.isMatchingStatus, value) }
              />

              <LntSelect
                { ...commonSelectProps }
                value={ filters.commentsByUsers }
                options={ commentsByUsersOptions }
                onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.commentsByUsers, value) }
                placeholder={ FILTERS_PLACEHOLDERS.commentsByUsers }
              />

              <TopFilterItem
                fullWide
                freeSizes
              >
                <LntCheckbox
                  value={ filters.isCHTM }
                  onChange={ handleCheckboxChange(FILTERS_PARAMS_KEYS.isCHTM, true) }
                >
                  ЧТМ
                </LntCheckbox>

                <LntCheckbox
                  className={ styles.group__item_checkbox }
                  value={ filters.isEPP }
                  onChange={ handleCheckboxChange(FILTERS_PARAMS_KEYS.isEPP, true) }
                >
                  ЕРР
                </LntCheckbox>

                <LntCheckbox
                  className={ styles.group__item_checkbox }
                  value={ filters.isInfo }
                  onChange={ handleCheckboxChange(FILTERS_PARAMS_KEYS.isInfo, true) }
                >
                  INFO
                </LntCheckbox>

                <LntCheckbox
                  className={ styles.group__item_checkbox }
                  value={ filters.isShortKvi }
                  onChange={ handleCheckboxChange(FILTERS_PARAMS_KEYS.isShortKvi, true) }
                >
                  ShortKvi
                </LntCheckbox>

                <LntCheckbox
                  className={ styles.group__item_checkbox }
                  value={ filters.isTop1 }
                  onChange={ handleCheckboxChange(FILTERS_PARAMS_KEYS.isTop1, true) }
                >
                  TOP1
                </LntCheckbox>

                { /* TODO: Заменить на реальный ключ, когда он будет готов на бэке */ }
                <LntCheckbox
                  className={ styles.group__item_checkbox }
                  // TODO: когда ключ будет согласован с беком, вынести его в словарь и заменить тут
                  value={ filters.tempMockBigMedia }
                  onChange={ handleCheckboxChange('tempMockBigMedia', true) }
                  disabled
                >
                  BigMedia
                </LntCheckbox>
              </TopFilterItem>
            </div>

            <LntDivider className={ styles.topFilters__divider } />

            <div className={ styles.topFilters__row }>
              <LntRangeDatePicker
                size={ SIZE.middle }
                format={ dateFormat.DD_MM_YYYY }
                onChange={ value =>
                  handleCreatedRangeDateChange(value, [
                    FILTERS_PARAMS_KEYS.startPromoDateAt,
                    FILTERS_PARAMS_KEYS.endPromoDateAt
                  ])
                }
                placeholder={ [FILTERS_PLACEHOLDERS.startPromoDateAt, FILTERS_PLACEHOLDERS.endPromoDateAt] }
              />

              <TopFilterItem className={ styles.topFiltersItem__separated }>
                <LntInput
                  value={ filters.minDiscount }
                  onChange={ event => handleInputFilterChange(FILTERS_PARAMS_KEYS.minDiscount, event) }
                  placeholder={ FILTERS_PLACEHOLDERS.minDiscount }
                  size={ SIZE.middle }
                  suffix='%'
                />

                <LntInput
                  value={ filters.maxDiscount }
                  onChange={ event => handleInputFilterChange(FILTERS_PARAMS_KEYS.maxDiscount, event) }
                  placeholder={ FILTERS_PLACEHOLDERS.maxDiscount }
                  size={ SIZE.middle }
                  suffix='%'
                />
              </TopFilterItem>

              <LntSelect
                { ...commonSelectProps }
                value={ filters.promoIds ?? [] }
                onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.promoIds, value) }
                placeholder={ FILTERS_PLACEHOLDERS.monitoringListsPromoFilter }
                querySelect={ {
                  queryKey: [MONITORINGS_LISTS_KEY.promotion],
                  queryFn: getMonitoringListsPromo,
                  select: getFilterFromEntitiesToLabelsIds
                } }
              />
            </div>
          </LntCollapsePanel>
        </LntCollapse>
      </div>
    </TopFiltersWrapper>
  )
}
